import { createContext, useCallback, useContext, useMemo, useState } from "react";
import { useToolParamPersistence } from "../../utils/toolParamPersistence";

export interface DataObject {
    name: string;
    traits: string[];
}

// Color palette - customize this array to change available colors for traits
// Colors are assigned deterministically based on trait names
export const TRAIT_COLOR_PALETTE = [
    "#FF6B6B", // Red
    "#4ECDC4", // Teal
    "#45B7D1", // Blue
    "#FFA07A", // Light Salmon
    "#24523B", // Mint
    "#DF3E23", // Purple
    "#85C1E2", // Sky Blue
    "#F8B739", // Orange
    "#52BE80", // Green
    "#EC7063", // Coral
    "#5DADE2", // Light Blue
    "#143464", // Pink
    "#14A02E", // Light Green
    "#AF7AC5", // Lavender
    "#FA6A0A", // Dark Orange
    "#1ABC9C", // Turquoise
    "#E74C3C", // Dark Red
    "#9B59B6", // Amethyst
    "#E67E22", // Carrot
    "#3498DB", // Bright Blue
    "#2ECC71", // Emerald
    "#F39C12", // Golden
    "#E91E63", // Pink
    "#9C27B0", // Deep Purple
    "#673AB7", // Indigo
    "#3F51B5", // Blue
    "#2196F3", // Light Blue
    "#00ACC1", // Darker Cyan
    "#009688", // Teal
    "#4CAF50", // Green
    "#66BB6A", // Medium Green
    "#689F38", // Dark Lime
    "#F9A825", // Dark Yellow
    "#FF8F00", // Dark Amber
    "#FF9800", // Orange
    "#FF5722", // Deep Orange
    "#795548", // Brown
    "#607D8B", // Blue Grey
    "#9E9E9E", // Grey
    "#34495E", // Dark Blue Grey
    "#16A085", // Dark Turquoise
    "#27AE60", // Dark Green
    "#2980B9", // Dark Blue
    "#8E44AD", // Dark Purple
    "#C0392B", // Dark Red
    "#D35400", // Dark Orange
    "#D68910", // Dark Golden Yellow
    "#2C3E50", // Very Dark Blue
    "#5D6D7E", // Medium Blue Grey
    "#6C757D", // Medium Grey
    "#A569BD", // Medium Purple
    "#5B2C6F", // Dark Violet
    "#1F618D", // Darker Blue
    "#117A65", // Darker Teal
    "#B7950B", // Darker Yellow
    "#922B21", // Darker Red
    "#76448A", // Darker Purple
    "#1B4F72", // Navy Blue
    "#0E6655", // Darker Green
    "#7D6608", // Olive
    "#6C3483", // Royal Purple
    "#1A5276", // Steel Blue
    "#0B5345", // Forest Green
    "#7B241C", // Maroon
    "#633974", // Deep Violet
    "#512E5F", // Very Dark Purple
    "#154360", // Midnight Blue
    "#641E16", // Very Dark Red
    "#4A235A", // Dark Magenta
    "#1B2631", // Almost Black
    "#566573", // Slate Grey
    "#A04000", // Burnt Orange
];

export interface Node {
    id: string;
    name: string;
    x: number;
    y: number;
}

export interface Arc {
    from: string;
    to: string;
    trait: string;
    angleOffset?: number; // Offset for bezier angle when multiple arcs exist between same nodes
}

interface TraitConnectionsState {
    data: DataObject[];
    nodes: Node[];
    arcs: Arc[];
    traitColors: Record<string, string>;
    nodeSize: number;
    nodeColor: string;
    arcStrokeWidth: number;
    showNodeLabels: boolean;
    showArcLabels: boolean;
    arcLabelFontSize: number;
    arcLabelUseArcColor: boolean;
    bezierAngle: number;
    initialLayoutType: "circle" | "grid" | "hexagonal";
    enableProximityLayout: boolean;
    proximityStrength: number;
    reorderByTraitCount: boolean;
    viewBoxAttr: string;
    error: string | null;
    loadDataFile: (file: File) => void;
    clearData: () => void;
    setNodeSize: (size: number) => void;
    setNodeColor: (color: string) => void;
    setArcStrokeWidth: (width: number) => void;
    setShowNodeLabels: (show: boolean) => void;
    setShowArcLabels: (show: boolean) => void;
    setArcLabelFontSize: (size: number) => void;
    setArcLabelUseArcColor: (use: boolean) => void;
    setBezierAngle: (angle: number) => void;
    setInitialLayoutType: (type: "circle" | "grid" | "hexagonal") => void;
    setEnableProximityLayout: (enable: boolean) => void;
    setProximityStrength: (strength: number) => void;
    setReorderByTraitCount: (reorder: boolean) => void;
    buildExportSvgString: () => string;
    handleDownload: () => void;
    handleDownloadHtml: () => void;
    applyPreset: (values: Record<string, unknown>) => void;
    getPresetValues: () => Record<string, unknown>;
    resetParameters: () => void;
}

const TraitConnectionsContext = createContext<TraitConnectionsState | null>(null);

export function useTraitConnections() {
    const context = useContext(TraitConnectionsContext);
    if (!context) {
        throw new Error(
            "useTraitConnections must be used within TraitConnectionsProvider"
        );
    }
    return context;
}

interface TraitConnectionsProviderProps {
    children: React.ReactNode;
    initialNodeSize?: number;
    initialNodeColor?: string;
    initialArcStrokeWidth?: number;
    initialShowNodeLabels?: boolean;
    initialShowArcLabels?: boolean;
    initialArcLabelFontSize?: number;
    initialArcLabelUseArcColor?: boolean;
    initialBezierAngle?: number;
    initialLayoutType?: "circle" | "grid" | "hexagonal";
    initialEnableProximityLayout?: boolean;
    initialProximityStrength?: number;
}

export function TraitConnectionsProvider({
    children,
    initialNodeSize = 8,
    initialNodeColor = "#ffffff",
    initialArcStrokeWidth = 2,
    initialShowNodeLabels = true,
    initialShowArcLabels = true,
    initialArcLabelFontSize = 12,
    initialArcLabelUseArcColor = false,
    initialBezierAngle = 45,
    initialLayoutType: initialLayoutTypeProp = "circle",
    initialEnableProximityLayout = false,
    initialProximityStrength = 50,
}: TraitConnectionsProviderProps) {
    const [data, setData] = useState<DataObject[]>([]);
    const [error, setError] = useState<string | null>(null);
    const [nodeSize, setNodeSize] = useState(initialNodeSize);
    const [nodeColor, setNodeColor] = useState(initialNodeColor);
    const [arcStrokeWidth, setArcStrokeWidth] = useState(initialArcStrokeWidth);
    const [showNodeLabels, setShowNodeLabels] = useState(initialShowNodeLabels);
    const [showArcLabels, setShowArcLabels] = useState(initialShowArcLabels);
    const [arcLabelFontSize, setArcLabelFontSize] = useState(initialArcLabelFontSize);
    const [arcLabelUseArcColor, setArcLabelUseArcColor] = useState(initialArcLabelUseArcColor);
    const [bezierAngle, setBezierAngle] = useState(initialBezierAngle);
    const [initialLayoutType, setInitialLayoutType] = useState<"circle" | "grid" | "hexagonal">(initialLayoutTypeProp);
    const [enableProximityLayout, setEnableProximityLayout] = useState(initialEnableProximityLayout);
    const [proximityStrength, setProximityStrength] = useState(initialProximityStrength);
    const [reorderByTraitCount, setReorderByTraitCount] = useState(false);

    const viewBoxAttr = "0 0 1200 1000";
    const centerX = 500;
    const centerY = 500;
    const radius = 350;

    // Get deterministic color from palette based on trait name
    const getTraitColor = (trait: string): string => {
        const hash = trait.split("").reduce((acc, char) => {
            return ((acc << 5) - acc + char.charCodeAt(0)) | 0;
        }, 0);
        
        // Use hash to select from palette
        const index = Math.abs(hash) % TRAIT_COLOR_PALETTE.length;
        return TRAIT_COLOR_PALETTE[index];
    };

    // Generate trait colors for all unique traits
    const traitColors = useMemo(() => {
        const colors: Record<string, string> = {};
        const uniqueTraits = new Set<string>();
        
        data.forEach((obj) => {
            obj.traits.forEach((trait) => uniqueTraits.add(trait));
        });
        
        uniqueTraits.forEach((trait) => {
            colors[trait] = getTraitColor(trait);
        });
        
        return colors;
    }, [data]);

    // Calculate number of shared traits between two nodes
    const getSharedTraitsCount = (obj1: DataObject, obj2: DataObject): number => {
        const traits1 = new Set(obj1.traits);
        const traits2 = new Set(obj2.traits);
        let count = 0;
        traits1.forEach((trait) => {
            if (traits2.has(trait)) count++;
        });
        return count;
    };

    // Reorder data by trait count if enabled, distributing evenly
    const orderedData = useMemo(() => {
        if (!reorderByTraitCount || data.length === 0) return data;
        
        // Sort by trait count (descending)
        const sorted = [...data].sort((a, b) => b.traits.length - a.traits.length);
        
        // Distribute evenly using recursive binary division
        // This spreads high-trait items throughout the entire layout space
        // Pattern: [middle, left-middle, right-middle, left-quarter, right-quarter, ...]
        const result: DataObject[] = new Array(sorted.length);
        const n = sorted.length;
        
        // Generate positions using breadth-first binary division
        const positions: number[] = [];
        const queue: Array<{ start: number; end: number }> = [{ start: 0, end: n - 1 }];
        
        while (queue.length > 0 && positions.length < n) {
            const { start, end } = queue.shift()!;
            const mid = Math.floor((start + end) / 2);
            positions.push(mid);
            
            // Add left and right halves to queue (breadth-first)
            if (start < mid) {
                queue.push({ start, end: mid - 1 });
            }
            if (mid < end) {
                queue.push({ start: mid + 1, end });
            }
        }
        
        // Place sorted items (highest trait count first) at evenly distributed positions
        sorted.forEach((item, sortedIdx) => {
            result[positions[sortedIdx]] = item;
        });
        
        return result;
    }, [data, reorderByTraitCount]);

    // Calculate node positions with initial layout, then optional proximity-based adjustment
    const nodes = useMemo(() => {
        if (orderedData.length === 0) return [];
        
        // Calculate initial positions based on layout type
        let initialNodes: Node[];
        
        if (initialLayoutType === "circle") {
            // Circular layout
            const angleStep = (2 * Math.PI) / orderedData.length;
            initialNodes = orderedData.map((obj, index) => {
                const angle = index * angleStep - Math.PI / 2; // Start at top
                const x = centerX + radius * Math.cos(angle);
                const y = centerY + radius * Math.sin(angle);
                
                return {
                    id: `node-${index}`,
                    name: obj.name,
                    x,
                    y,
                };
            });
        } else if (initialLayoutType === "grid") {
            // Grid layout - place nodes equidistantly
            const nodeCount = orderedData.length;
            const padding = 100;
            const availableWidth = 1000 - 2 * padding;
            const availableHeight = 1000 - 2 * padding;
            
            // Calculate optimal grid dimensions to fill the space
            // Try different aspect ratios to find the best fit
            const aspectRatio = availableWidth / availableHeight;
            let bestCols = Math.ceil(Math.sqrt(nodeCount * aspectRatio));
            let bestRows = Math.ceil(nodeCount / bestCols);
            
            // Try a few variations to find the best fit
            for (let testCols = Math.max(1, Math.floor(Math.sqrt(nodeCount * aspectRatio))); 
                 testCols <= Math.ceil(Math.sqrt(nodeCount / aspectRatio) * 2); 
                 testCols++) {
                const testRows = Math.ceil(nodeCount / testCols);
                // Prefer grids that are closer to square and use space efficiently
                const currentWaste = (testCols * testRows) - nodeCount;
                const bestWaste = (bestCols * bestRows) - nodeCount;
                if (currentWaste < bestWaste || (currentWaste === bestWaste && Math.abs(testCols / testRows - 1) < Math.abs(bestCols / bestRows - 1))) {
                    bestCols = testCols;
                    bestRows = testRows;
                }
            }
            
            const cols = bestCols;
            const rows = bestRows;
            
            // Calculate spacing to fill the available space
            const spacingX = cols > 1 ? availableWidth / (cols - 1) : 0;
            const spacingY = rows > 1 ? availableHeight / (rows - 1) : 0;
            
            initialNodes = orderedData.map((obj, index) => {
                const col = index % cols;
                const row = Math.floor(index / cols);
                const x = padding + col * spacingX;
                const y = padding + row * spacingY;
                
                return {
                    id: `node-${index}`,
                    name: obj.name,
                    x,
                    y,
                };
            });
        } else {
            // Hexagonal grid layout
            const nodeCount = orderedData.length;
            const padding = 100;
            const availableWidth = 1000 - 2 * padding;
            const availableHeight = 1000 - 2 * padding;
            
            // Calculate optimal hexagon size and grid dimensions
            // Start with a reasonable hex size and adjust to fit all nodes
            const aspectRatio = availableWidth / availableHeight;
            
            // Try different hex sizes to find one that fits well
            let bestCols = 1;
            let bestRows = 1;
            let bestHexSize = 40;
            
            for (let hexSize = 30; hexSize <= 80; hexSize += 5) {
                const hexWidth = hexSize * 2;
                const hexHeight = hexSize * Math.sqrt(3);
                const cols = Math.floor(availableWidth / (hexWidth * 0.75)) || 1;
                const rows = Math.floor(availableHeight / hexHeight) || 1;
                const totalCells = cols * rows;
                
                if (totalCells >= nodeCount) {
                    // This size can fit all nodes
                    const waste = totalCells - nodeCount;
                    const currentWaste = bestCols * bestRows - nodeCount;
                    if (waste < currentWaste || (waste === currentWaste && Math.abs(cols / rows - aspectRatio) < Math.abs(bestCols / bestRows - aspectRatio))) {
                        bestCols = cols;
                        bestRows = rows;
                        bestHexSize = hexSize;
                    }
                }
            }
            
            const cols = bestCols;
            const rows = bestRows;
            const hexSize = bestHexSize;
            const hexWidth = hexSize * 2;
            const hexHeight = hexSize * Math.sqrt(3);
            
            // Calculate spacing to center the grid
            const totalHexWidth = (cols - 1) * (hexWidth * 0.75) + (rows % 2 === 1 ? 0 : hexWidth * 0.375);
            const totalHexHeight = (rows - 1) * hexHeight;
            const startX = padding + (availableWidth - totalHexWidth) / 2;
            const startY = padding + (availableHeight - totalHexHeight) / 2;
            
            initialNodes = orderedData.map((obj, index) => {
                // Calculate which hexagon cell this node belongs to
                const col = index % cols;
                const row = Math.floor(index / cols);
                
                // Hexagonal grid: every other row is offset
                const offsetX = row % 2 === 1 ? hexWidth * 0.375 : 0;
                const x = startX + col * (hexWidth * 0.75) + offsetX;
                const y = startY + row * hexHeight;
                
                return {
                    id: `node-${index}`,
                    name: obj.name,
                    x,
                    y,
                };
            });
        }

        if (!enableProximityLayout || proximityStrength === 0) {
            return initialNodes;
        }

        // Apply force-directed layout based on shared traits
        const strength = proximityStrength / 100; // 0-1
        const iterations = 50;
        const damping = 0.9;
        const minDistance = 50; // Minimum distance between nodes
        
        const positions = initialNodes.map((node) => ({ x: node.x, y: node.y }));
        
        for (let iter = 0; iter < iterations; iter++) {
            const forces = positions.map(() => ({ fx: 0, fy: 0 }));
            
            // Calculate forces between all pairs of nodes
            for (let i = 0; i < orderedData.length; i++) {
                for (let j = i + 1; j < orderedData.length; j++) {
                    const sharedTraits = getSharedTraitsCount(orderedData[i], orderedData[j]);
                    if (sharedTraits === 0) continue;
                    
                    const dx = positions[j].x - positions[i].x;
                    const dy = positions[j].y - positions[i].y;
                    const distance = Math.sqrt(dx * dx + dy * dy);
                    
                    if (distance < 0.1) continue; // Avoid division by zero
                    
                    // Attraction force based on shared traits
                    // More shared traits = stronger attraction
                    const attractionStrength = (sharedTraits / 10) * strength; // Normalize by max expected traits
                    const force = attractionStrength * Math.min(distance, radius * 2) / radius;
                    
                    const fx = (dx / distance) * force;
                    const fy = (dy / distance) * force;
                    
                    forces[i].fx -= fx;
                    forces[i].fy -= fy;
                    forces[j].fx += fx;
                    forces[j].fy += fy;
                }
            }
            
            // Apply repulsion to prevent nodes from overlapping
            for (let i = 0; i < orderedData.length; i++) {
                for (let j = i + 1; j < orderedData.length; j++) {
                    const dx = positions[j].x - positions[i].x;
                    const dy = positions[j].y - positions[i].y;
                    const distance = Math.sqrt(dx * dx + dy * dy);
                    
                    if (distance < minDistance && distance > 0.1) {
                        const repulsion = (minDistance - distance) / minDistance;
                        const fx = (dx / distance) * repulsion * 5;
                        const fy = (dy / distance) * repulsion * 5;
                        
                        forces[i].fx -= fx;
                        forces[i].fy -= fy;
                        forces[j].fx += fx;
                        forces[j].fy += fy;
                    }
                }
            }
            
            // Apply forces with damping
            for (let i = 0; i < positions.length; i++) {
                positions[i].x += forces[i].fx * damping;
                positions[i].y += forces[i].fy * damping;
                
                // Keep nodes within reasonable bounds
                const distFromCenter = Math.sqrt(
                    Math.pow(positions[i].x - centerX, 2) + 
                    Math.pow(positions[i].y - centerY, 2)
                );
                if (distFromCenter > radius * 1.5) {
                    const scale = (radius * 1.5) / distFromCenter;
                    positions[i].x = centerX + (positions[i].x - centerX) * scale;
                    positions[i].y = centerY + (positions[i].y - centerY) * scale;
                }
            }
        }
        
        return initialNodes.map((node, index) => ({
            ...node,
            x: positions[index].x,
            y: positions[index].y,
        }));
    }, [orderedData, initialLayoutType, enableProximityLayout, proximityStrength]);

    // Generate arcs between nodes sharing traits
    const arcs = useMemo(() => {
        if (nodes.length < 2) return [];
        
        const arcMap = new Map<string, Arc>();
        
        // For each unique trait, find all nodes that have it
        const traitToNodes = new Map<string, string[]>();
        
        orderedData.forEach((obj, index) => {
            obj.traits.forEach((trait) => {
                if (!traitToNodes.has(trait)) {
                    traitToNodes.set(trait, []);
                }
                traitToNodes.get(trait)!.push(`node-${index}`);
            });
        });
        
        // For each trait, create arcs between all pairs of nodes
        traitToNodes.forEach((nodeIds, trait) => {
            for (let i = 0; i < nodeIds.length; i++) {
                for (let j = i + 1; j < nodeIds.length; j++) {
                    const from = nodeIds[i];
                    const to = nodeIds[j];
                    // Create unique key for arc (order-independent)
                    const arcKey = from < to ? `${from}-${to}-${trait}` : `${to}-${from}-${trait}`;
                    
                    if (!arcMap.has(arcKey)) {
                        arcMap.set(arcKey, {
                            from,
                            to,
                            trait,
                        });
                    }
                }
            }
        });
        
        // Group arcs by node pair and assign angle offsets
        const arcsByNodePair = new Map<string, Arc[]>();
        const allArcs = Array.from(arcMap.values());
        
        allArcs.forEach((arc) => {
            const pairKey = arc.from < arc.to ? `${arc.from}-${arc.to}` : `${arc.to}-${arc.from}`;
            if (!arcsByNodePair.has(pairKey)) {
                arcsByNodePair.set(pairKey, []);
            }
            arcsByNodePair.get(pairKey)!.push(arc);
        });
        
        // Assign angle offsets to arcs that share the same node pair
        arcsByNodePair.forEach((arcsForPair) => {
            if (arcsForPair.length > 1) {
                // Multiple arcs between same nodes - assign different angles
                const angleSpread = 15; // degrees of spread per arc
                const startOffset = -((arcsForPair.length - 1) * angleSpread) / 2;
                
                arcsForPair.forEach((arc, index) => {
                    arc.angleOffset = startOffset + index * angleSpread;
                });
            } else {
                // Single arc - no offset needed
                arcsForPair[0].angleOffset = 0;
            }
        });
        
        return allArcs;
    }, [orderedData, nodes]);

    const loadDataFile = (file: File) => {
        setError(null);
        
        if (!file.name.toLowerCase().endsWith(".json")) {
            setError("Please choose a JSON file.");
            return;
        }
        
        const reader = new FileReader();
        reader.onload = () => {
            const text = typeof reader.result === "string" ? reader.result : "";
            
            if (!text) {
                setError("Unable to read file.");
                return;
            }
            
            try {
                const parsed = JSON.parse(text);
                
                if (!Array.isArray(parsed)) {
                    setError("JSON file must contain an array of objects.");
                    return;
                }
                
                // Validate structure
                const validated: DataObject[] = [];
                for (let i = 0; i < parsed.length; i++) {
                    const item = parsed[i];
                    if (!item || typeof item !== "object") {
                        setError(`Item at index ${i} is not an object.`);
                        return;
                    }
                    if (typeof item.name !== "string") {
                        setError(`Item at index ${i} is missing a valid 'name' field.`);
                        return;
                    }
                    if (!Array.isArray(item.traits)) {
                        setError(`Item at index ${i} is missing a valid 'traits' array.`);
                        return;
                    }
                    validated.push({
                        name: item.name,
                        traits: item.traits.filter((t: unknown) => typeof t === "string"),
                    });
                }
                
                // Count trait occurrences across all items
                const traitCounts = new Map<string, number>();
                validated.forEach((item) => {
                    item.traits.forEach((trait) => {
                        traitCounts.set(trait, (traitCounts.get(trait) || 0) + 1);
                    });
                });
                
                // Filter out traits that only appear in one item
                const sharedTraits = new Set<string>();
                traitCounts.forEach((count, trait) => {
                    if (count > 1) {
                        sharedTraits.add(trait);
                    }
                });
                
                // Remove unique traits from each item
                const filtered: DataObject[] = validated.map((item) => ({
                    name: item.name,
                    traits: item.traits.filter((trait) => sharedTraits.has(trait)),
                }));
                
                setData(filtered);
            } catch (e) {
                setError(`Failed to parse JSON: ${e instanceof Error ? e.message : String(e)}`);
            }
        };
        
        reader.onerror = () => {
            setError("Error reading file.");
        };
        
        reader.readAsText(file);
    };

    const clearData = () => {
        setData([]);
        setError(null);
    };

    const getBezierPath = (
        x1: number,
        y1: number,
        x2: number,
        y2: number,
        angleOffset: number = 0
    ): string => {
        const dx = x2 - x1;
        const dy = y2 - y1;
        const distance = Math.sqrt(dx * dx + dy * dy);
        const lineAngle = Math.atan2(dy, dx);
        
        const angleRad = ((bezierAngle + angleOffset) * Math.PI) / 180;
        const controlDistance = distance * 0.4;
        
        const cp1Angle = lineAngle + angleRad;
        const cp2Angle = lineAngle - angleRad + Math.PI;
        
        const cp1x = x1 + Math.cos(cp1Angle) * controlDistance;
        const cp1y = y1 + Math.sin(cp1Angle) * controlDistance;
        const cp2x = x2 + Math.cos(cp2Angle) * controlDistance;
        const cp2y = y2 + Math.sin(cp2Angle) * controlDistance;
        
        return `M ${x1} ${y1} C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${x2} ${y2}`;
    };

    const getBezierMidpoint = (
        x1: number,
        y1: number,
        x2: number,
        y2: number,
        angleOffset: number = 0
    ): { x: number; y: number } => {
        const dx = x2 - x1;
        const dy = y2 - y1;
        const distance = Math.sqrt(dx * dx + dy * dy);
        const lineAngle = Math.atan2(dy, dx);
        
        const angleRad = ((bezierAngle + angleOffset) * Math.PI) / 180;
        const controlDistance = distance * 0.4;
        
        const cp1Angle = lineAngle + angleRad;
        const cp1x = x1 + Math.cos(cp1Angle) * controlDistance;
        const cp1y = y1 + Math.sin(cp1Angle) * controlDistance;
        const cp2Angle = lineAngle - angleRad + Math.PI;
        const cp2x = x2 + Math.cos(cp2Angle) * controlDistance;
        const cp2y = y2 + Math.sin(cp2Angle) * controlDistance;
        
        // Calculate midpoint of bezier curve at t=0.5
        const t = 0.5;
        const mt = 1 - t;
        const x = mt * mt * mt * x1 + 3 * mt * mt * t * cp1x + 3 * mt * t * t * cp2x + t * t * t * x2;
        const y = mt * mt * mt * y1 + 3 * mt * mt * t * cp1y + 3 * mt * t * t * cp2y + t * t * t * y2;
        
        return { x, y };
    };

    const buildExportSvgString = (): string => {
        if (nodes.length === 0) return "";
        
        // Build arcs markup
        const arcsMarkup = arcs
            .map((arc) => {
                const fromNode = nodes.find((n) => n.id === arc.from);
                const toNode = nodes.find((n) => n.id === arc.to);
                if (!fromNode || !toNode) return "";
                
                const angleOffset = arc.angleOffset || 0;
                const pathData = getBezierPath(
                    fromNode.x,
                    fromNode.y,
                    toNode.x,
                    toNode.y,
                    angleOffset
                );
                const color = traitColors[arc.trait] || "#000000";
                
                let markup = `    <path d="${pathData}" stroke="${color}" stroke-width="${arcStrokeWidth}" fill="none" style="mix-blend-mode: multiply" data-trait="${arc.trait}" />`;
                
                if (showArcLabels) {
                    const midpoint = getBezierMidpoint(
                        fromNode.x,
                        fromNode.y,
                        toNode.x,
                        toNode.y,
                        angleOffset
                    );
                    const labelColor = arcLabelUseArcColor ? color : "#000000";
                    markup += `\n    <text x="${midpoint.x}" y="${midpoint.y}" fill="${labelColor}" font-size="${arcLabelFontSize}" font-family="Arial" text-anchor="middle" dominant-baseline="middle" data-trait="${arc.trait}">${arc.trait}</text>`;
                }
                
                return markup;
            })
            .filter(Boolean)
            .join("\n");
        
        // Build nodes markup
        const nodesMarkup = nodes
            .map((node) => {
                let markup = `    <circle cx="${node.x}" cy="${node.y}" r="${nodeSize}" fill="${nodeColor}" stroke="#333333" stroke-width="1" />`;
                
                if (showNodeLabels) {
                    markup += `\n    <text x="${node.x}" y="${node.y + nodeSize + 15}" fill="#333333" font-size="14" font-family="Arial" font-weight="bold" text-anchor="middle" dominant-baseline="middle">${node.name}</text>`;
                }
                
                return markup;
            })
            .join("\n");
        
        // Build legend markup
        const uniqueTraits = Object.keys(traitColors).sort((a, b) => a.localeCompare(b));
        const legendHeight = uniqueTraits.length * 20 + 10;
        let legendMarkup = "";
        if (uniqueTraits.length > 0) {
            // Position legend in bottom left, but shifted right to avoid overlap
            const legendX = 1020; // Position legend to the right of the main content
            legendMarkup = `  <g class="legend">
    <rect x="${legendX}" y="${1000 - legendHeight}" width="200" height="${legendHeight}" fill="white" stroke="#333333" stroke-width="1" opacity="0.9" />`;
            uniqueTraits.forEach((trait, idx) => {
                const y = 1000 - legendHeight + 10 + idx * 20;
                const color = traitColors[trait];
                legendMarkup += `
    <g class="legend-item" data-trait="${trait}">
        <rect x="${legendX + 10}" y="${y}" width="15" height="15" fill="${color}" stroke="#333333" stroke-width="1" />
        <text x="${legendX + 30}" y="${y + 10}" fill="#333333" font-size="12" font-family="Arial" dominant-baseline="middle">${trait}</text>
    </g>`;
            });
            legendMarkup += `
  </g>`;
        }

        return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="${viewBoxAttr}">
  <g class="arcs">
${arcsMarkup}
  </g>
  <g class="nodes">
${nodesMarkup}
  </g>
${legendMarkup}
</svg>`;
    };

    const handleDownload = () => {
        if (nodes.length === 0) return;
        
        const svgString = buildExportSvgString();
        if (!svgString) return;
        
        const blob = new Blob([svgString], {
            type: "image/svg+xml;charset=utf-8",
        });
        const url = URL.createObjectURL(blob);
        
        const link = document.createElement("a");
        link.href = url;
        link.download = `trait-connections-${Date.now()}.svg`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    const handleDownloadHtml = () => {
        if (nodes.length === 0) return;
        
        let svgString = buildExportSvgString();
        if (!svgString) return;
        
        // Add explicit width and height to SVG for proper rendering in HTML
        // viewBox is "0 0 1200 1000", so we'll use those dimensions
        svgString = svgString.replace(
            /<svg([^>]*)>/,
            '<svg$1 width="1200" height="1000">'
        );
        
        // Extract unique traits for the hover script
        const uniqueTraits = Object.keys(traitColors).sort((a, b) => a.localeCompare(b));
        
        // Generate HTML with embedded SVG and hover script
        const htmlContent = `<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Trait Connections</title>
    <style>
        body {
            margin: 0;
            padding: 20px;
            font-family: Arial, sans-serif;
            background-color: #f5f5f5;
            display: flex;
            justify-content: center;
            align-items: center;
            min-height: 100vh;
        }
        .container {
            background: white;
            padding: 20px;
            border-radius: 8px;
            box-shadow: 0 2px 8px rgba(0,0,0,0.1);
        }
        svg {
            display: block;
            max-width: 100%;
            height: auto;
        }
        .legend-item {
            cursor: pointer;
        }
        .legend-item:hover {
            opacity: 0.8;
        }
    </style>
</head>
<body>
    <div class="container">
        ${svgString}
    </div>
    <script>
        (function() {
            const arcs = document.querySelectorAll('.arcs path');
            const arcLabels = document.querySelectorAll('.arcs text');
            const legendItems = document.querySelectorAll('.legend-item');
            
            // Add hover handlers to legend items
            legendItems.forEach((legendItem) => {
                const trait = legendItem.getAttribute('data-trait');
                if (!trait) return;
                
                legendItem.addEventListener('mouseenter', function() {
                    arcs.forEach(arc => {
                        const arcTrait = arc.getAttribute('data-trait');
                        if (arcTrait !== trait) {
                            arc.style.opacity = '0.2';
                        } else {
                            arc.style.opacity = '1';
                        }
                    });
                    arcLabels.forEach(label => {
                        const labelTrait = label.getAttribute('data-trait');
                        if (labelTrait !== trait) {
                            label.style.opacity = '0.2';
                        } else {
                            label.style.opacity = '1';
                        }
                    });
                });
                
                legendItem.addEventListener('mouseleave', function() {
                    arcs.forEach(arc => {
                        arc.style.opacity = '1';
                    });
                    arcLabels.forEach(label => {
                        label.style.opacity = '1';
                    });
                });
            });
        })();
    </script>
</body>
</html>`;
        
        const blob = new Blob([htmlContent], {
            type: "text/html;charset=utf-8",
        });
        const url = URL.createObjectURL(blob);
        
        const link = document.createElement("a");
        link.href = url;
        link.download = `trait-connections-${Date.now()}.html`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    const getPresetValues = useCallback(
        (): Record<string, unknown> => ({
            nodeSize, nodeColor, arcStrokeWidth, showNodeLabels, showArcLabels,
            arcLabelFontSize, arcLabelUseArcColor, bezierAngle,
            initialLayoutType, enableProximityLayout, proximityStrength,
            reorderByTraitCount,
        }),
        [
            nodeSize, nodeColor, arcStrokeWidth, showNodeLabels, showArcLabels,
            arcLabelFontSize, arcLabelUseArcColor, bezierAngle,
            initialLayoutType, enableProximityLayout, proximityStrength,
            reorderByTraitCount,
        ],
    );

    const applyPreset = useCallback((values: Record<string, unknown>) => {
        if (values.nodeSize != null) setNodeSize(values.nodeSize as number);
        if (values.nodeColor != null) setNodeColor(values.nodeColor as string);
        if (values.arcStrokeWidth != null) setArcStrokeWidth(values.arcStrokeWidth as number);
        if (values.showNodeLabels != null) setShowNodeLabels(values.showNodeLabels as boolean);
        if (values.showArcLabels != null) setShowArcLabels(values.showArcLabels as boolean);
        if (values.arcLabelFontSize != null) setArcLabelFontSize(values.arcLabelFontSize as number);
        if (values.arcLabelUseArcColor != null) setArcLabelUseArcColor(values.arcLabelUseArcColor as boolean);
        if (values.bezierAngle != null) setBezierAngle(values.bezierAngle as number);
        if (values.initialLayoutType != null) setInitialLayoutType(values.initialLayoutType as "circle" | "grid" | "hexagonal");
        if (values.enableProximityLayout != null) setEnableProximityLayout(values.enableProximityLayout as boolean);
        if (values.proximityStrength != null) setProximityStrength(values.proximityStrength as number);
        if (values.reorderByTraitCount != null) setReorderByTraitCount(values.reorderByTraitCount as boolean);
    }, []);

    const { resetParameters } = useToolParamPersistence({
        toolId: "trait-connections",
        defaultPreset: {
            nodeSize: initialNodeSize,
            nodeColor: initialNodeColor,
            arcStrokeWidth: initialArcStrokeWidth,
            showNodeLabels: initialShowNodeLabels,
            showArcLabels: initialShowArcLabels,
            arcLabelFontSize: initialArcLabelFontSize,
            arcLabelUseArcColor: initialArcLabelUseArcColor,
            bezierAngle: initialBezierAngle,
            initialLayoutType: initialLayoutTypeProp,
            enableProximityLayout: initialEnableProximityLayout,
            proximityStrength: initialProximityStrength,
            reorderByTraitCount: false,
        },
        getPresetValues,
        applyPreset,
    });

    return (
        <TraitConnectionsContext.Provider
            value={{
                data,
                nodes,
                arcs,
                traitColors,
                nodeSize,
                nodeColor,
                arcStrokeWidth,
                showNodeLabels,
                showArcLabels,
                arcLabelFontSize,
                arcLabelUseArcColor,
                bezierAngle,
                viewBoxAttr,
                error,
                loadDataFile,
                clearData,
                setNodeSize,
                setNodeColor,
                setArcStrokeWidth,
                setShowNodeLabels,
                setShowArcLabels,
                setArcLabelFontSize,
                setArcLabelUseArcColor,
                setBezierAngle,
                initialLayoutType,
                setInitialLayoutType,
                enableProximityLayout,
                proximityStrength,
                setEnableProximityLayout,
                setProximityStrength,
                reorderByTraitCount,
                setReorderByTraitCount,
                buildExportSvgString,
                handleDownload,
                handleDownloadHtml,
                applyPreset,
                getPresetValues,
                resetParameters,
            }}
        >
            {children}
        </TraitConnectionsContext.Provider>
    );
}
